import { useAppUser, usePlanning, useWorkPackageOwner } from '@bechtel/client-common';
import { CommodityMilestone, MilestoneStatusCode } from '@bechtel/types';
import { isWithinInterval, parseISO } from 'date-fns';
import { concat, filter, findIndex, includes, map, some } from 'lodash';
import { usePlanningPermissions } from '../../../hooks';

//commodityMilestones already grouped based on commodityid from calling component
export function useDragPermission(commodityMilestones: CommodityMilestone[]): boolean {
  const { canPlanAssignedMilestones, canPlanAreaMilestones, canPlanAllMilestones } = usePlanningPermissions();
  const { user } = useAppUser();
  const userFacilities = user?.facilities ? user?.facilities : [];
  const { weekStartDate, weekEndDate, selectedForeman, aiPlannedMilestoneCount } = usePlanning();

  // Extract workPackage safely before any early returns
  const workPackage =
    commodityMilestones?.[0]?.commodity?.weld?.workPackage ||
    commodityMilestones?.[0]?.commodity?.materialCommodity?.workPackage;
  const workOwners = workPackage
    ? concat(workPackage.primaryOwners || [], workPackage.secondaryOwners || [], workPackage.otherOwners || [])
    : [];

  // Call all hooks unconditionally (Rules of Hooks requirement)
  const isOwner = useWorkPackageOwner(workOwners);

  // Lock milestones while AI is processing (PROCESSING) or when completed with planned milestones awaiting user action (Save/Cancel)
  if (
    selectedForeman?.planningAiRequest?.status === 'PROCESSING' ||
    (selectedForeman?.planningAiRequest?.status === 'COMPLETED' && aiPlannedMilestoneCount)
  ) {
    return false;
  }

  // Rest of the validation logic requires valid data
  if (!commodityMilestones?.length || !workPackage) {
    return false;
  }

  const { startDate: wpStartDate, endDate: wpEndDate } = workPackage;
  const workPackageFacilities = workPackage.facilities ? workPackage.facilities : [];
  const isFacilityOwner =
    !workPackageFacilities || workPackageFacilities.length == 0 || !userFacilities || userFacilities.length == 0
      ? false
      : some(workPackageFacilities, (wpf) =>
          includes(
            map(userFacilities, (userFacility) => userFacility.id),
            wpf.id
          )
        );

  if (
    !canPlanAllMilestones &&
    (!isOwner ||
      findIndex(
        workOwners,
        (owner) => owner && owner.crewMember && owner.crewMember.id && owner.crewMember.id == selectedForeman?.id
      ) == -1)
  )
    return false;

  const parsedWpStartDate = wpStartDate && parseISO(wpStartDate);
  const parsedWpEndDate = wpEndDate && parseISO(wpEndDate);
  const parsedWeekStartDate = weekStartDate && parseISO(weekStartDate);
  const parsedWeekEndDate = weekEndDate && parseISO(weekEndDate);
  const isPlannable =
    parsedWpStartDate &&
    parsedWpEndDate &&
    parsedWeekStartDate &&
    parsedWeekEndDate &&
    parsedWpStartDate < parsedWpEndDate &&
    (isWithinInterval(parsedWpStartDate, { start: parsedWeekStartDate, end: parsedWeekEndDate }) ||
      isWithinInterval(parsedWpEndDate, { start: parsedWeekStartDate, end: parsedWeekEndDate }) ||
      isWithinInterval(parsedWeekStartDate, { start: parsedWpStartDate, end: parsedWpEndDate }) ||
      isWithinInterval(parsedWeekEndDate, { start: parsedWpStartDate, end: parsedWpEndDate }));
  if (!isPlannable) return false;

  const plannableMilestones = filter(commodityMilestones, (item: CommodityMilestone) => {
    return (
      item.currentStatus.codeI18n === MilestoneStatusCode.UNPLANNED ||
      item.currentStatus.codeI18n === MilestoneStatusCode.PLANNED ||
      item.currentStatus.codeI18n === MilestoneStatusCode.REJECTED ||
      item.currentStatus.codeI18n === MilestoneStatusCode.BLOCKED
    );
  });

  return (
    (canPlanAllMilestones || (canPlanAssignedMilestones && isOwner) || (canPlanAreaMilestones && isFacilityOwner)) &&
    isPlannable &&
    plannableMilestones.length === commodityMilestones.length
  );
}
